import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { getApiCall, postApiCall } from "../../service/httpCall";
import { API_PROMPT_ACTIVE_LIST } from "../../service/url";
import { PROMPT_STATUS, USER_ROLES } from "../../utilities/constant";
import Loader from "../../components/partials/components/loader";
import { toast } from "react-toastify";
import { InputText } from "primereact/inputtext";
import { FaPlay, FaCheck, FaTimes } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";

import { Storage } from "../../utilities/setting";

const PendingPromptsTable = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState("");
  const [showAudio, setShowAudio] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [actionType, setActionType] = useState("");
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const AUTO_REFRESH_SECONDS = 30;
  const [refreshCountdownSec, setRefreshCountdownSec] =
    useState(AUTO_REFRESH_SECONDS);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem("user");
      if (!rawUser) {
        console.error("User not found in Storage");
        return null;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error("Invalid JSON", e);
        return null;
      }
      if (user.role) setLoggedInUserRole(user.role);
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    fetchPrompts(true);

    const refreshInterval = setInterval(() => {
      fetchPrompts(false);
      setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
    }, AUTO_REFRESH_SECONDS * 1000);

    const countdownInterval = setInterval(() => {
      setRefreshCountdownSec((prev) => {
        if (prev <= 1) {
          return AUTO_REFRESH_SECONDS;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [loggedInUserRole]);

  const fetchPrompts = (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }
    getApiCall(`${API_PROMPT_ACTIVE_LIST}?prompt_status=0`)
      .then((resp) => {
        const updated = resp.map((item, index) => ({
          ...item,
          key: index + 1,
          status: "Pending",
        }));
        setPrompts(updated);
      })
      .catch((err) => toast.error(err))
      .finally(() => setLoading(false));
  };

  const openConfirm = (id, type) => {
    setSelectedId(id);
    setActionType(type);
    setConfirmVisible(true);
  };

  const handleConfirm = () => {
    if (!selectedId) return;
    const payload =
      actionType === "approve"
        ? { status: PROMPT_STATUS.APPROVED }
        : { status: PROMPT_STATUS.REJECT };
    postApiCall(`/v2/prompt/${selectedId}`, payload)
      .then(() => {
        actionType === "approve"
          ? toast.success("Prompt Approved")
          : toast.warning("Prompt Rejected");
        setPrompts((prev) => prev.filter((p) => p.id !== selectedId));
      })
      .catch((err) => toast.error(err))
      .finally(() => {
        setConfirmVisible(false);
        setSelectedId(null);
        setActionType("");
      });
  };

  const handleApprove = (id) => {
    postApiCall(`/v2/prompt/${id}`, { status: PROMPT_STATUS.APPROVED })
      .then(() => {
        toast.success("Prompt Approved");
        setPrompts((prev) => prev.filter((p) => p.id !== id));
      })
      .catch((err) => toast.error(err));
  };

  const handleReject = (id) => {
    postApiCall(`/v2/prompt/${id}`, { status: PROMPT_STATUS.REJECT })
      .then(() => {
        toast.warning("Prompt Rejected");
        setPrompts((prev) => prev.filter((p) => p.id !== id));
      })
      .catch((err) => toast.error(err));
  };

  const handleListen = (url) => {
    setAudioUrl(url);
    setShowAudio(true);
  };

  const actionTemplate = (row) => (
    <div style={{ display: "flex", gap: 6 }}>
      <button
        className="dash-action-btn dash-action-btn--listen"
        onClick={() => handleListen(row.prompt_url)}
        title="Listen"
      >
        <FaPlay size={11} /> Listen
      </button>
      <button
        className="dash-action-btn dash-action-btn--approve"
        onClick={() => openConfirm(row.id, "approve")}
        title="Approve"
      >
        <FaCheck size={11} /> Approve
      </button>
      <button
        className="dash-action-btn dash-action-btn--reject"
        onClick={() => openConfirm(row.id, "reject")}
        title="Reject"
      >
        <FaTimes size={11} /> Reject
      </button>
    </div>
  );

  if (loading) return <Loader />;

  return (
    <>
      <div className="dash-section-card" style={{ marginBottom: 28 }}>
        <div
          className="dash-section-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span>Pending Prompts</span>

            {prompts.length > 0 && (
              <span className="dash-count-badge">{prompts.length}</span>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              fetchPrompts(false);
              setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
            }}
            style={{
              border: "1px solid var(--dash-border)",
              background: "var(--dash-surface-alt)",
              color: "var(--dash-text-soft)",
              borderRadius: 999,
              height: 32,
              padding: "0 12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
            title="Refresh Pending Prompts"
          >
            <FiRefreshCw size={13} />
            {refreshCountdownSec}s
          </button>
        </div>

        <div className="dash-table-toolbar">
          <div className="dash-search-wrap">
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search prompts…"
              className="dash-search-input"
            />
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <DataTable
            value={prompts}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            globalFilter={globalFilter}
            className="dash-datatable"
            emptyMessage="No pending prompts"
          >
            <Column field="prompt_name" header="Name" sortable />
            <Column field="prompt_duration" header="Duration" sortable />
            <Column field="prompt_category" header="Category" sortable />
            <Column field="creator.name" header="Created By" sortable />
            <Column
              header="Actions"
              body={actionTemplate}
              style={{ minWidth: "7rem" }}
            />
          </DataTable>
        </div>
      </div>

      {/* Audio dialog */}
      <Dialog
        header="Audio Playback"
        visible={showAudio}
        style={{ width: "min(90vw, 420px)" }}
        onHide={() => setShowAudio(false)}
        className="dash-dialog"
      >
        <audio controls style={{ width: "100%", marginTop: 8 }}>
          <source src={audioUrl} type="audio/mpeg" />
        </audio>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog
        header="Confirm Action"
        visible={confirmVisible}
        style={{ width: "min(90vw, 360px)" }}
        onHide={() => setConfirmVisible(false)}
        className="dash-dialog"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              paddingTop: 4,
            }}
          >
            <button
              className="dash-action-btn dash-action-btn--neutral"
              onClick={() => setConfirmVisible(false)}
            >
              Cancel
            </button>
            <button
              className={`dash-action-btn ${actionType === "approve" ? "dash-action-btn--approve" : "dash-action-btn--reject"}`}
              onClick={handleConfirm}
            >
              {actionType === "approve" ? "Approve" : "Reject"}
            </button>
          </div>
        }
      >
        <p style={{ color: "var(--dash-text)", margin: 0 }}>
          Are you sure you want to{" "}
          <strong
            style={{ color: actionType === "approve" ? "#10b981" : "#f43f5e" }}
          >
            {actionType === "approve" ? "approve" : "reject"}
          </strong>{" "}
          this prompt?
        </p>
      </Dialog>
    </>
  );
};

export default PendingPromptsTable;
